// ─── Season Rollover Engine (V3 multi-season) ────────────────────────────────
//
// "Start New Season" archives the current season's live stats into the player's
// per-season history map, then resets the live fields to zero so the team begins
// the new season fresh — carrying the full roster over. Nothing is deleted:
// old games stay in Firestore (season-tagged) and the archived totals power the
// read-only Season History view.
//
// Non-destructive & backward-compatible: players without a `seasonStats` map
// simply get one created; games written before season-tagging are backfilled
// during the rollover.

import {
  doc,
  writeBatch,
  getDocs,
  collection,
  type Firestore,
} from 'firebase/firestore';
import type { ArchivedSeason, Player } from '../types/dugout';

/** Canonical season key, e.g. seasonKey("Spring", 2026) → "2026-Spring". */
export function seasonKey(season: string, year: number): string {
  return `${year}-${season}`;
}

/** The zeroed live-`stats` shape. Also written by Clear This Season's Stats (clear-season-stats.ts). */
export function emptyStats(): Record<string, number> {
  return { gp: 0, pa: 0, ab: 0, avg: 0, obp: 0, ops: 0, h: 0, bb: 0, so: 0, rbi: 0, r: 0 };
}

/**
 * Build the archive snapshot + the live-field reset patch for one player.
 * Pure: returns the data; the caller decides how to persist it. Captures every
 * live field that accumulates across a season so nothing is orphaned.
 */
export function archivePlayerForRollover(
  player: Player & Record<string, any>,
  season: string,
  year: number,
): { archive: ArchivedSeason; resetPatch: Record<string, any> } {
  const archive: ArchivedSeason = {
    season,
    year,
    stats: player.stats ?? emptyStats(),
    pitchingLog: player.pitchingLog ?? [],
    benchLog: player.benchLog ?? {},
    seasonBenchInnings: player.seasonBenchInnings ?? 0,
    gameBallLog: player.gameBallLog ?? {},
    seasonGameBalls: player.seasonGameBalls ?? 0,
    passedBallLog: player.passedBallLog ?? {},
    seasonPassedBalls: player.seasonPassedBalls ?? 0,
    blockLog: player.blockLog ?? {},
    seasonBlocks: player.seasonBlocks ?? 0,
    archivedAt: new Date().toISOString(),
  };

  const key = seasonKey(season, year);

  // Reset all live fields. We also mirror the archived batting totals into
  // `priorSeasonStats` so the xOVR engine's existing baseline fallback keeps
  // working unchanged — a freshly-reset player leans on last season until the
  // new season accrues enough PA/IP.
  //
  // `seasonStats` is written as a nested object (NOT a dotted "seasonStats.KEY"
  // path): set(..., {merge:true}) deep-merges nested maps, so this adds the new
  // key without clobbering previously-archived seasons. A dotted key would be
  // taken literally by set() and create a field whose name contains a dot.
  const resetPatch: Record<string, any> = {
    seasonStats: { [key]: archive },
    priorSeasonStats: {
      season,
      year,
      stats: archive.stats,
      fieldTime: player.seasonSnapshot?.fieldTime ?? player.priorSeasonStats?.fieldTime ?? {},
    },
    stats: emptyStats(),
    pitchingLog: [],
    benchLog: {},
    seasonBenchInnings: 0,
    gameBallLog: {},
    seasonGameBalls: 0,
    passedBallLog: {},
    seasonPassedBalls: 0,
    blockLog: {},
    seasonBlocks: 0,
    // Clear the current-season full-season import override. Its totals are
    // preserved inside the archive above.
    seasonSnapshot: null,
    updatedAt: new Date().toISOString(),
  };

  return { archive, resetPatch };
}

export interface StartNewSeasonArgs {
  db: Firestore;
  team: { id: string; season: string; seasonYear: number } & Record<string, any>;
  players: (Player & Record<string, any>)[];
  newSeason: string;
  newYear: number;
}

/**
 * Run the full rollover: backfill season tags on the outgoing season's games,
 * archive + reset every player, and bump the team to the new season. Writes are
 * batched and chunked at 450 ops/batch (Firestore's limit is 500) so large
 * rosters/schedules commit safely.
 */
export async function startNewSeason({
  db,
  team,
  players,
  newSeason,
  newYear,
}: StartNewSeasonArgs): Promise<void> {
  const outgoingSeason = team.season;
  // Older team docs may store seasonYear as a string — coerce so the archive
  // year and seasonKey are consistently numeric.
  const outgoingYear = Number(team.seasonYear) || new Date().getFullYear();

  // Collect all writes, then flush in chunks.
  const ops: { ref: ReturnType<typeof doc>; data: Record<string, any> }[] = [];

  // 1. Backfill season tags on any untagged games so the outgoing season's games
  //    are attributable in the Season History view. Tagged games are left as-is.
  const gamesSnap = await getDocs(collection(db, `teams/${team.id}/games`));
  gamesSnap.forEach((g) => {
    const data = g.data() as Record<string, any>;
    if (data.season == null || data.seasonYear == null) {
      ops.push({
        ref: doc(db, `teams/${team.id}/games`, g.id),
        data: { season: outgoingSeason, seasonYear: outgoingYear },
      });
    }
  });

  // 2. Archive + reset every player.
  for (const player of players) {
    const { resetPatch } = archivePlayerForRollover(player, outgoingSeason, outgoingYear);
    ops.push({
      ref: doc(db, `teams/${team.id}/players`, player.id),
      data: resetPatch,
    });
  }

  // 3. Bump the team to the new season.
  ops.push({
    ref: doc(db, 'teams', team.id),
    data: { season: newSeason, seasonYear: newYear, updatedAt: new Date().toISOString() },
  });

  // 4. Flush in chunks of 450.
  const CHUNK = 450;
  for (let i = 0; i < ops.length; i += CHUNK) {
    const batch = writeBatch(db);
    for (const { ref, data } of ops.slice(i, i + CHUNK)) {
      batch.set(ref, data, { merge: true });
    }
    await batch.commit();
  }
}
